<template>
  <v-form ref="form" v-model="valid">
    <h3 class="title mb-4">Tenancy Details</h3>
    
    <v-row>
      <v-col cols="12" md="6">
        <v-select
          v-model="tenancyData.stage"
          :items="tenancyStages"
          label="Tenancy Stage"
          outlined
          dense
          item-text="text"
          item-value="value"
          :rules="[rules.required]"
          required
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-select
          v-model="tenancyData.serviceTypeId"
          :items="serviceTypeOptions"
          label="Service Type"
          outlined
          dense
          item-text="text"
          item-value="value"
          :rules="[rules.required]"
          required
        />
      </v-col>
    </v-row>
    
    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          v-model.number="tenancyData.advertisedRent"
          label="Advertised Rent (£)"
          outlined
          dense
          type="number"
          prefix="£"
          :rules="[rules.required, rules.positiveNumber]"
          required
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model.number="tenancyData.deposit"
          label="Deposit (£)"
          outlined
          dense
          type="number"
          prefix="£"
          :rules="[rules.required, rules.positiveNumber]"
          required
        />
      </v-col>
    </v-row>
    
    <!-- Date fields - only shown for Let Agreed or Signed stages -->
    <div v-if="showDateFields">
      <v-divider class="my-4" />
      <h4 class="subtitle-1 mb-3">Tenancy Dates</h4>
      
      <v-row>
        <v-col cols="12" md="6">
          <v-menu
            v-model="startDateMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="tenancyData.startDate"
                label="Start Date"
                prepend-icon="mdi-calendar"
                readonly
                outlined
                dense
                v-bind="attrs"
                v-on="on"
                :rules="showDateFields ? [rules.required] : []"
              />
            </template>
            <v-date-picker
              v-model="tenancyData.startDate"
              @input="startDateMenu = false"
            />
          </v-menu>
        </v-col>
        <v-col cols="12" md="6">
          <v-menu
            v-model="fixedDateMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="tenancyData.fixedDate"
                label="Fixed Term End Date"
                prepend-icon="mdi-calendar"
                readonly
                outlined
                dense
                v-bind="attrs"
                v-on="on"
                :rules="showDateFields ? [rules.required, rules.afterStartDate] : []"
              />
            </template>
            <v-date-picker
              v-model="tenancyData.fixedDate"
              @input="fixedDateMenu = false; updateEndDate()"
              :min="tenancyData.startDate"
            />
          </v-menu>
        </v-col>
      </v-row>
      
      <!-- Periodic Tenancy Question -->
      <v-row v-if="tenancyData.fixedDate">
        <v-col cols="12">
          <v-checkbox
            v-model="becomesPeriodicAfterFixed"
            label="Does this tenancy become periodic after the fixed term?"
            dense
            @change="updateEndDate"
          />
          <div class="text-caption ml-8 mt-n2">
            <span v-if="becomesPeriodicAfterFixed">The tenancy will continue on a rolling basis after the fixed term ends.</span>
            <span v-else>The tenancy will end on {{ formatDate(tenancyData.fixedDate) }}.</span>
          </div>
        </v-col>
      </v-row>
      
    </div>
    
    <!-- Navigation buttons -->
    <v-row class="mt-6">
      <v-col cols="6">
        <v-btn
          text
          @click="$emit('back')"
        >
          <v-icon left>mdi-arrow-left</v-icon>
          Back
        </v-btn>
      </v-col>
      <v-col cols="6" class="text-right">
        <v-btn
          color="primary"
          :disabled="!canProceed"
          @click="proceed"
        >
          Next
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'TenancyDetailsStep',
  
  props: {
    value: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      valid: false,
      startDateMenu: false,
      fixedDateMenu: false,
      becomesPeriodicAfterFixed: true,
      tenancyData: this.value,
      tenancyStages: [
        { value: 0, text: 'New Instruction' },
        { value: 1, text: 'Advertised' },
        { value: 2, text: 'Let Agreed' },
        { value: 3, text: 'Signed' }
      ],
      rules: {
        required: v => !!v || 'This field is required',
        positiveNumber: v => {
          if (v === null || v === '') return 'This field is required'
          return v > 0 || 'Amount must be greater than 0'
        },
        afterStartDate: v => {
          if (!this.tenancyData.startDate || !v) return true
          return v >= this.tenancyData.startDate || 'Fixed date must be after start date'
        }
      }
    }
  },
  
  computed: {
    showDateFields() {
      // Show date fields for Let Agreed (2) or Signed (3)
      return this.tenancyData.stage === 2 || this.tenancyData.stage === 3
    },
    
    ...mapGetters('TenanciesManagement', ['serviceTypeOptions']),
    
    canProceed() {
      const baseValid = this.valid && 
                       this.tenancyData.stage !== null &&
                       this.tenancyData.advertisedRent > 0 &&
                       this.tenancyData.deposit >= 0 &&
                       this.tenancyData.serviceTypeId
      
      if (this.showDateFields) {
        // For Signed tenancies (3), all dates are required
        if (this.tenancyData.stage === 3) {
          return baseValid && 
                 this.tenancyData.startDate && 
                 this.tenancyData.fixedDate &&
                 this.tenancyData.fixedDate >= this.tenancyData.startDate
        }
        
        // For Let Agreed (2), dates are optional
        if (this.tenancyData.startDate || this.tenancyData.fixedDate) {
          return baseValid && 
                 this.tenancyData.startDate && 
                 this.tenancyData.fixedDate &&
                 this.tenancyData.fixedDate >= this.tenancyData.startDate
        }
        
        return baseValid
      }
      
      return baseValid
    }
  },
  
  watch: {
    value: {
      deep: true,
      handler(newVal) {
        this.tenancyData = newVal
      }
    },
    
    tenancyData: {
      deep: true,
      handler(val) {
        this.$emit('input', val)
      }
    },
    
    'tenancyData.stage'(newVal, oldVal) {
      // Clear date fields if moving to a stage that doesn't require them
      if (oldVal !== null && (newVal === 0 || newVal === 1)) {
        this.tenancyData.startDate = null
        this.tenancyData.fixedDate = null
        this.tenancyData.endDate = null
      }
    }
  },
  
  methods: {
    updateEndDate() {
      if (this.becomesPeriodicAfterFixed) {
        // If tenancy becomes periodic, end date should be null
        this.tenancyData.endDate = null
      } else if (!this.becomesPeriodicAfterFixed && this.tenancyData.fixedDate) {
        // If NOT periodic, end date should match fixed date
        this.tenancyData.endDate = this.tenancyData.fixedDate
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB')
    },
    
    proceed() {
      if (this.canProceed) {
        this.$emit('next')
      }
    }
  }
}
</script>